// 主人公の普段の致命 = 刺した点から青白い火花が放射状に散る (2026-10-21・A案・配布版の既定)。
//   ユーザーが手本の絵 A案を ok (答えるページ luna-exec-v2-1021)。手本 = そざい/concept/luna-exec-1021b/a_scene.png・a_frames.png
//   (刺した瞬間／0.2秒／0.6秒)。元の映像 = Lies of P の致命 (刺した点から火花が放射状に散る)。記録 = docs/research/2026-10-21-luna-exec-fx-a.md。
// 骨組み: 刺した点 (首元) の小さな白い一点から、青白い粒が約60、ボスの背中側と上へ勢いよく散る。0.6秒後に床へ少し残る。
//   色は常夜燈の青白だけ。赤・橙・金、灰・煙、白い効果線、大きな丸い閃光、リボン、主人公へ流れる光、ボスの全身の光は使わない。
// 目に刺さらない散り方 (10-21 パリィの FX に「チカチカして目がめちゃくちゃ疲れる」):
//   点滅させない (粒ごとの明るさの揺らぎ・ちらつき無し)・一瞬だけ強く光る物を作らない (白い一点も 0.03 秒で灯ってなめらかに消える)・
//   粒は伸ばしすぎない (速い粒でも長さ 1.8 倍まで = 線に見せない・10-21 の撮影で 2.2 倍は白い線に見えた)・出る時刻を 0.08 秒に散らす (全部が同じコマで出ない)。
// 粒の質感 = 手本の絵から切り出した絵 (黒背景・加算で黒を消す・public/fx/luna-spark/・切り出し scripts/slice_luna_spark_sprites.py)。
//   飛ぶ・落ちる・床に残る・消えるはコード。どれも時刻だけで決まる (乱数は種つき) = 同じ時刻なら同じ絵。
import { drawAdditive } from './exec_fx_sprites'
import type { CrackInput } from './luna_lamp_leak_fx'

export const SPARK_KINDS = 8
export const DOT_KINDS = 4

type Frame = { img: HTMLImageElement | null }
const sparks: Frame[] = Array.from({ length: SPARK_KINDS }, () => ({ img: null }))
const dots: Frame[] = Array.from({ length: DOT_KINDS }, () => ({ img: null }))
const core: Frame = { img: null }

let started = false
export function preloadSparkSprites(prefix = '/fx/luna-spark/'): void {
  if (started || typeof Image === 'undefined') return
  started = true
  const load = (f: Frame, name: string): void => {
    const img = new Image()
    img.onload = () => { f.img = img }
    img.src = `${prefix}${name}.png`
  }
  sparks.forEach((f, i) => load(f, `spark_${i}`))
  dots.forEach((f, i) => load(f, `dot_${i}`))
  load(core, 'core')
}
export function sparkSpritesReady(): boolean {
  return core.img !== null && sparks.every(f => f.img !== null) && dots.every(f => f.img !== null)
}

/** 数・速さ・時間の決まり (px は人の背丈 = 1 の時)。 */
export const SPARK = {
  /** 背中側と上へ散る粒・床へ落ちて残る粒・主人公側へ少しだけこぼれる粒 (計 60)。 */
  spray: 40,
  floor: 16,
  back: 4,
  /** 出る時刻の散らばり (秒)。 */
  birthSpread: 0.08,
  /** 飛び出す速さ px/秒 (下限・上限)。空気で止まる強さ (1/秒)・重さ px/秒²。 */
  speedMin: 260,
  speedMax: 1150,
  drag: 4.2,
  gravity: 520,
  /** 粒の大きさ: 太さ px・長さは太さ × 1.8、速さで最大 1.8 倍まで伸びる。3粒に1粒は丸い粒 (手本の細かいきらめき)。 */
  width: 4.8,
  stretchMax: 1.8,
  /** 空中の粒の寿命 (下限・上限)。 */
  lifeMin: 0.3,
  lifeMax: 0.85,
  /** 床に残る粒: 落ちてから消え切るまで (前半はそのまま灯り、後半でなめらかに消える)。 */
  restSec: 0.9,
  dotSize: 6,
  /** 刺した点の白い一点: 灯る・消え切る (秒)・大きさ px。 */
  coreIn: 0.03,
  coreOut: 0.32,
  coreSize: 34,
  /** 刺した点のまわりの淡い青のにじみ (手本 0.2 秒のコマ)。強さの頂点・消え切る (秒)・大きさ px・強さ。 */
  hazePeak: 0.07,
  hazeOut: 0.42,
  hazeSize: 110,
  hazeAlpha: 0.6,
  lifeSec: 1.6,
} as const

/** 量の段 (10-22 作者「ちょっと量が少ない気がする…今の少ないとした時に、普通、多い、大量で分けて作ってみて」)。
 *  形・色・速さ・寿命・白い一点とにじみは変えず、粒の数だけを掛ける。
 *  配布版の既定 = max (大量・360 粒。10-23 作者の答え「大量だけ」)。開発版 ?lunaSparkAmount=low|mid|high で前の量 (low = 10-22 までの 60 粒)。 */
export type SparkAmount = 'low' | 'mid' | 'high' | 'max'
export const SPARK_AMOUNT: Record<SparkAmount, number> = { low: 1, mid: 2, high: 3.5, max: 6 }
let amount: SparkAmount = 'max'
/** game.ts の URL ブロックだけが呼ぶ。知らない値・指定なし = max (配布版)。 */
export function setSparkAmount(v: string | null | undefined): SparkAmount {
  amount = v === 'low' || v === 'mid' || v === 'high' ? v : 'max'
  return amount
}
export function sparkAmount(): SparkAmount { return amount }
/** 段ごとの粒の数 (背中側と上へ・床へ・主人公側へ)。 */
export function sparkCounts(a: SparkAmount = amount): { spray: number; floor: number; back: number } {
  const m = SPARK_AMOUNT[a]
  return { spray: Math.round(SPARK.spray * m), floor: Math.round(SPARK.floor * m), back: Math.round(SPARK.back * m) }
}

const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}

type Spark = { born: number; vx: number; vy: number; life: number; kind: number; floor: boolean; w: number }

/** 粒の初めの向き・速さ。away = 主人公から離れる側 (刺した向き)。y は下が正。 */
function sparkOf(i: number, S: number, away: 1 | -1, n: { spray: number; floor: number }): Spark {
  const born = rnd(i, 1) * SPARK.birthSpread * (i < 8 ? 0.25 : 1) // 最初の数粒はほぼ同時 (刺さった手応え)
  const kind = Math.floor(rnd(i, 2) * SPARK_KINDS)
  const w = SPARK.width * S * (0.7 + rnd(i, 3) * 0.6)
  let ang: number // 0 = 離れる側の真横、+ = 上
  let sp = SPARK.speedMin + (SPARK.speedMax - SPARK.speedMin) * Math.pow(rnd(i, 4), 0.8)
  let floor = false
  let life = SPARK.lifeMin + (SPARK.lifeMax - SPARK.lifeMin) * rnd(i, 5)
  if (i < n.spray) {
    // 背中側と上: 真横から真上の少し先まで。斜め上 (45度前後) に多く寄せる
    const u = rnd(i, 6)
    ang = (-0.2 + 1.95 * (0.5 + (u - 0.5) * Math.abs(u - 0.5) * 2)) // -11〜100度
  } else if (i < n.spray + n.floor) {
    // 床へ: 離れる側の斜め下。床で止まって残る
    ang = -(0.35 + rnd(i, 6) * 0.9)
    sp *= 0.7
    floor = true
    life = 9 // 床に着くまで生きる
  } else {
    // 主人公側へ少しだけ: 遅く・短く (主人公へ流れる光にしない)
    ang = Math.PI - (0.5 + rnd(i, 6) * 0.9)
    sp *= 0.35
    life *= 0.6
  }
  return { born, vx: Math.cos(ang) * sp * away * S, vy: -Math.sin(ang) * sp * S, life, kind, floor, w }
}

/** 時刻 t の位置と速さ (空気で止まり、重さで少し落ちる)。 */
function flight(p: Spark, t: number, S: number): { dx: number; dy: number; vx: number; vy: number } {
  const k = SPARK.drag
  const e = Math.exp(-k * t)
  const g = SPARK.gravity * S
  const dx = p.vx * (1 - e) / k
  const dy = p.vy * (1 - e) / k + g * (t / k - (1 - e) / (k * k))
  return { dx, dy, vx: p.vx * e, vy: p.vy * e + g * (1 - e) / k }
}

/** 床に着く時刻 (二分法・着かなければ null)。 */
function landTime(p: Spark, drop: number, S: number): number | null {
  if (drop <= 0) return 0
  let lo = 0
  let hi = 1.2
  if (flight(p, hi, S).dy < drop) return null
  for (let n = 0; n < 18; n++) {
    const m = (lo + hi) / 2
    if (flight(p, m, S).dy < drop) lo = m
    else hi = m
  }
  return hi
}

/** 普段の致命の光 (世界の層・カメラの内側で描く)。入力は前の版のひびと同じ。 */
export function drawLunaSparkFx(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  const age = c.ageSec
  if (age < 0 || age > SPARK.lifeSec) return
  const S = c.scale
  const away = c.dir
  const n = sparkCounts()
  const total = n.spray + n.floor + n.back
  for (let i = 0; i < total; i++) drawSpark(ctx, c, sparkOf(i, S, away, n), age, S, i)
  drawHaze(ctx, c.x, c.y, age, S)
  drawCore(ctx, c.x, c.y, age, S)
}

/** 刺した点の淡い青のにじみ: ゆっくり灯ってなめらかに消える (大きな閃光にしない・弱い)。絵は青い縁の丸い粒を大きく置く。 */
function drawHaze(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, S: number): void {
  if (age > SPARK.hazeOut) return
  const u = age < SPARK.hazePeak ? age / SPARK.hazePeak : 1 - (age - SPARK.hazePeak) / (SPARK.hazeOut - SPARK.hazePeak)
  const a = u * u * (3 - 2 * u) * SPARK.hazeAlpha
  const size = SPARK.hazeSize * S * (0.85 + 0.15 * clamp01(age / SPARK.hazePeak))
  drawAdditive(ctx, dots[0]!.img!, x, y, size, size, 0.5, 0.5, a)
}

function drawCore(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, S: number): void {
  if (age > SPARK.coreOut) return
  const img = core.img!
  const a = age < SPARK.coreIn ? age / SPARK.coreIn : 1 - (age - SPARK.coreIn) / (SPARK.coreOut - SPARK.coreIn)
  const size = SPARK.coreSize * S * (0.8 + 0.2 * clamp01(age / SPARK.coreIn))
  drawAdditive(ctx, img, x, y, size, size, 0.5, 0.5, a * 0.85)
}

function drawSpark(ctx: CanvasRenderingContext2D, c: CrackInput, p: Spark, age: number, S: number, i: number): void {
  const t = age - p.born
  if (t < 0) return
  const drop = c.floorY - c.y
  if (p.floor) {
    const land = landTime(p, drop, S)
    if (land !== null && t >= land) {
      // 床に残る: 落ちた所で小さな粒になり、なめらかに消える
      const r = t - land
      if (r > SPARK.restSec) return
      const at = flight(p, land, S)
      const img = dots[i % DOT_KINDS]!.img!
      const size = SPARK.dotSize * S * (0.7 + rnd(i, 8) * 0.6)
      const fade = clamp01((r / SPARK.restSec - 0.4) / 0.6)
      const a = (1 - fade * fade) * 0.9
      drawAdditive(ctx, img, c.x + at.dx + at.vx * 0.012, c.floorY - 1, size, size, 0.5, 0.5, a)
      return
    }
  }
  if (t > p.life) return
  const f = flight(p, t, S)
  if (f.dy > drop) return // 床より下へは出さない
  const u = t / p.life
  const speed = Math.hypot(f.vx, f.vy)
  const stretch = 1 + Math.min(SPARK.stretchMax - 1, speed / (700 * S))
  const round = i % 3 === 0
  const len = round ? p.w * 1.3 : p.w * 1.8 * stretch
  // 出る時は 0.04 秒で灯り、終わり 4 割でなめらかに消える (点滅させない)
  const a = clamp01(t / 0.04) * (1 - clamp01((u - 0.6) / 0.4))
  // 遅くなった粒は少し縮む (遠くへ散って見える)
  const shrink = 0.75 + 0.25 * (1 - u)
  if (round) {
    const img = dots[p.kind % DOT_KINDS]!.img!
    drawAdditive(ctx, img, c.x + f.dx, c.y + f.dy, len * shrink, len * shrink, 0.5, 0.5, a)
    return
  }
  drawAdditive(ctx, sparks[p.kind]!.img!, c.x + f.dx, c.y + f.dy, len * shrink, p.w * shrink * 1.4, 0.5, 0.5, a, Math.atan2(f.vy, f.vx))
}
